import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { fetchMe } from '@/api/me';
import { useAuthStore } from '@/app/store/auth.store';

// SHCT-629 — re-read the caller's permissions from GET /auth/me on every
// protected navigation.
//
// Before this, `permissions` was written once (login, or bootstrapSession on a
// hard reload) and never refreshed. When a Root changed someone's Role while
// they were logged in, their client kept the old map: menus they no longer had
// stayed in the LNB, write buttons stayed enabled, and the refusal only arrived
// from the server after the click. PRD 21 §7 defines three layers — L1 disabled
// UI, L2 click interceptor, L3 server re-check — and only L3 was still telling
// the truth.
//
// A navigation is the right moment to re-sync: it is when the guard runs anyway,
// it costs one small request, and it bounds staleness to a single page view
// rather than to the whole 60-minute session.
export function usePermissionSync(): void {
  const { pathname } = useLocation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const syncPermissions = useAuthStore((s) => s.syncPermissions);
  const markPermissionsSynced = useAuthStore((s) => s.markPermissionsSynced);
  // StrictMode runs effects twice in dev, so the same path must not fire twice.
  //
  // SHCT-629 (re-test) — this used to be a plain `inFlight` boolean, which also swallowed the
  // sync for a DIFFERENT path when one was already in the air. That path then never settled, and
  // since the gate now waits for a settle, its page would sit blank. Keying the guard on the path
  // keeps the StrictMode protection without dropping a real navigation.
  const requestedPath = useRef<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated || requestedPath.current === pathname) return;
    requestedPath.current = pathname;
    let active = true;
    void fetchMe()
      .then((me) => {
        if (!active) return;
        syncPermissions({
          roles: me.roles,
          isRoot: me.isRoot,
          status: me.status,
          permissions: me.permissions,
        });
      })
      .catch(() => {
        // Deliberately silent, and the one place in this repo where that is the
        // right call: nothing the user did started this request, so a toast
        // would appear out of nowhere on a transient network blip. Keeping the
        // last known map is also the safe failure mode — it never widens access,
        // and the server (L3) still refuses anything the user has since lost.
        // A genuine 401 is not swallowed here; the axios interceptor owns it.
      })
      .finally(() => {
        // Settled either way. The gate is waiting on this, so it must run on the failure path too
        // — otherwise a transient network blip leaves the page with nothing rendered at all.
        if (active) markPermissionsSynced(pathname);
      });
    return () => {
      active = false;
      // The navigation was abandoned mid-flight. Release the guard so returning to this path
      // re-reads rather than reusing a request whose result was thrown away.
      if (requestedPath.current === pathname) requestedPath.current = null;
    };
  }, [pathname, isAuthenticated, syncPermissions, markPermissionsSynced]);
}
